import { createTheme } from '@mui/material/styles';

export const colorTokens = {
  default: {
    primary: '#ed1c24',
    secondary: '#0074D9',
    background: '#fff',
    surface: '#fff',
    backgroundred: '#fdedeb',
    backgroundblue: '#f1f7ff',
    backgroundheader: '#000',
    text: '#000'
  },
  light: {
    primary: '#d1191f',
    secondary: '#0074D9',
    background: '#f0f0e5',
    surface: '#fcfcf3',
    backgroundred: '#fdedeb',
    backgroundblue: '#f1f7ff',
    backgroundheader: '#262626',
    text: '#101010'
  },
  dark: {
    primary: '#ed1c24',
    secondary: '#4da3ff',
    background: '#0f172a',
    surface: '#1e293b',
    backgroundred: '#2b1511',
    backgroundblue: '#0f1d30',
    backgroundheader: '#d6d6d5',
    text: '#d6d6d5'

  },
};

export const designTokens = {
  fonts: {
    heading: "'Bebas Neue', Arial, sans-serif",
    body: "'Roboto', 'Helvetica', 'Arial', sans-serif",
  },
  sizes: {
    baseFontSize: 12, // or '16px' or '1rem'
    headingFontSize: 24,
    titleFontSize: 40,
  },
}

export const getAppTheme = (mode = 'light') => {
  const resolvedMode = mode === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : mode;

  const c = colorTokens[resolvedMode] ?? colorTokens.light; // Fallback to light if something goes wrong
  const muiMode = resolvedMode === "dark" ? "dark" : "light";
  const fonts = designTokens.fonts;
  const sizes = designTokens.sizes;

  return createTheme({
    palette: {
      mode: muiMode,
      primary: { main: c.primary },
      secondary: { main: c.secondary },
      background: { default: c.background, paper: c.surface },
      text: { primary: c.text },
    },
    typography: {
      fontFamily: fonts.body,
      fontSize: sizes.baseFontSize,
      /*body1: { fontSize: '1.5rem' },*/
      title: { fontFamily: fonts.heading, letterSpacing: '0.5px', fontSize: sizes.titleFontSize },
      h1: { fontFamily: fonts.heading, letterSpacing: '0.5px', fontSize: sizes.headingFontSize },
      h2: { fontFamily: fonts.heading, letterSpacing: '0.5px', fontSize: sizes.headingFontSize - 4 },
      h3: {
        fontFamily: fonts.body,
        letterSpacing: '0.5px',
        fontSize: sizes.baseFontSize + 2,
        fontStyle: 'italic',
        fontWeight: '600',
      },
      numbers: {
        fontFamily: 'var(--font-mono, monospace)',
        fontSize: sizes.baseFontSize + 2,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontFamily: fonts.body,
          },
          h1: {
            fontFamily: fonts.heading,
            letterSpacing: '0.5px',
            fontSize: '2.2em',
          },
          h2: {
            fontFamily: fonts.heading,
            letterSpacing: '0.5px',
            fontSize: '1.8em',
          },
          h3: {
            fontFamily: fonts.heading,
            letterSpacing: '0.5px',
            fontSize: '1.4em',
            marginTop: '0.2em',
            marginBottom: '0.2em',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: fonts.heading,
            letterSpacing: '0.5px',
            fontSize: '1.4rem',
            borderRadius: 0,
            textTransform: 'none',
            '&:hover, &:focus-visible': {
              color: c.text,
            },
          },
          containedPrimary: {
            backgroundColor: c.primary,
            borderColor: c.primary,
            '&:hover, &:active, &:focus': {
              backgroundColor: c.primary,
              borderColor: c.primary,
            },
          },
          outlinedPrimary: {
            color: c.primary,
            borderColor: c.primary,
            '&:hover, &:active, &:focus': {
              color: c.primary,
              borderColor: c.primary,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          fullWidth: true,
          size: 'small',
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            fontSize: sizes.baseFontSize + 2,
          },
        },
      },
      MuiFormHelperText: {
        styleOverrides: {
          root: {
            minHeight: 20, // keeps helper/error space consistent
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: 'var(--color-surface)',
          },
        },
      },
    },
  });
};

export const applyCssVars = (mode = 'light') => {
  const resolvedMode =
    mode === "system"
      ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
      : mode;

  const c = colorTokens[resolvedMode] ?? colorTokens.light; // Fallback to light if something goes wrong
  const fonts = designTokens.fonts;
  const root = document.documentElement;

  root.setAttribute('data-theme', resolvedMode);
  root.style.setProperty('--color-primary', c.primary);
  root.style.setProperty('--color-secondary', c.secondary);
  root.style.setProperty('--color-background', c.background);
  root.style.setProperty('--color-bg-red', c.backgroundred);
  root.style.setProperty('--color-surface', c.surface);
  root.style.setProperty('--color-bg-blue', c.backgroundblue);
  root.style.setProperty('--color-bg-header', c.backgroundheader);
  root.style.setProperty('--color-text', c.text);
  root.style.setProperty('--font-body', fonts.body);
  root.style.setProperty('--font-heading', fonts.heading);
};

